import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {createTheme, ThemeProvider, CssBaseline, Box, Typography} from "@mui/material";

// Config Imports
import { auth } from "../firebase/firebaseConfig";
import { AuthProvider } from "../components/context/AuthContext";

// Layout & Guards
import Layout from "../components/layout/Layout";
import ProtectedRoute from "./ProtectedRoute";
import RoleRoute from "./RoleRoute";

// Page Imports
import Home from "../pages/Home";
import About from "../pages/About";
import Login from "../pages/Login";
import Signup from "../pages/signup";
import Profile from "../pages/Profile";
import Dashboard from "../pages/Dashboard";
import AdminDashboard from "../pages/AdminDashBoard";
import UserDashboard from "../pages/UserDashBoard";
import EducationalCourses from "../pages/EducationalCourses";
import LanguageCourses from "../pages/LanguageCourses";
import PlanApplicationPage from "../pages/PlanApplicationPage";

// Component/Utility Imports
import ContactForm from "../components/ContactForm";
import ApplicationPage from "../components/ApplicationPage";

// SIA & Facility Courses
import SIACourseOverview from "../Courses/SIACourses/SIACourseOverview";
import DoorSupervisorDashboard from "../Courses/SIACourses/DoorSupervisorDashboard";
import SecurityGuardDashboard from "../Courses/SIACourses/SecurityGuardDashboard";
import CCTVOperatorDashboard from "../Courses/SIACourses/CCTVOperatorDashboard";
import DoorSupervisorQuizPage from "../siaquizes/DoorSupervisorQuizPage";
import FacilitiesManagementOverview from "../Courses/FacilityCourses/FacilitiesManagementOverview";

// Language Pages
import NigeriaLanguagePage from "../pages/NigeriaLanguagePage";
import FinnishLanguagePage from "../pages/FinnishLanguagePage";
import GermanyLanguage from "../pages/GermanLanguage";
import ChineseLanguage from "../pages/ChineseLanguage";
import AdvancedEnglishProfessional from "../pages/AdvancedEnglishProfessional";

// University Pages
import CanterburyChristChurchUni from "../Schools/CanterburyChristChurchUni";
import ApexCollegePage from "../Schools/ApexCollegePage";
import LSST from "../Schools/LSST";
import RoehamptonPage from "../Schools/RoehamptonPage";

// --- Theme Configuration (Const Style) ---

/**
 * Professional Theme Update:
 * Switched from "Dark Mode" logic to a custom "Sky Mode"
 * using #BBE0EF for the main primary accents.
 */
const getAppTheme = (isSkyMode) => createTheme({
    palette: {
        mode: isSkyMode ? "light" : "light", // Keeping base light for professional clarity
        primary: {
            main: "#09122C", // Deep Navy for authority
        },
        secondary: {
            main: "#BBE0EF", // Your requested Sky Blue
        },
        background: {
            default: isSkyMode ? "#BBE0EF" : "#F8F9FA", // Toggle background color
            paper: "#FFFFFF",
        },
    },
    typography: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        h1: { fontWeight: 800 },
        button: { textTransform: "none", fontWeight: 700 },
    },
    shape: {
        borderRadius: 12,
    },
});

// --- Main App Component ---

const App = () => {
    // 1. Authentication State
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    // 2. Custom Theme State (Toggles between White and Sky Blue)
    const [skyMode, setSkyMode] = useState(false);
    const theme = getAppTheme(skyMode);

    return (
        <ThemeProvider theme={theme}>
            {/* CssBaseline ensures the background color #BBE0EF applies to the whole body */}
            <CssBaseline />
            <AuthProvider>
                <Router>
                    {/* Layout receives 'skyMode' instead of 'darkMode' */}
                    <Layout user={user} darkMode={skyMode} setDarkMode={setSkyMode}>
                        <Routes>
                            {/* Redirect root to /home */}
                            <Route path="/" element={<Navigate to="/home" replace />} />

                            {/* Public Academic Routes */}
                            <Route path="/home" element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/educational-courses" element={<EducationalCourses />} />
                            <Route path="/language-courses" element={<LanguageCourses />} />
                            <Route path="/contact-form" element={<ContactForm />} />
                            <Route path="/application-page" element={<ApplicationPage />} />
                            <Route path="/plan-application-page" element={<PlanApplicationPage />} />

                            {/* Authentication */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />

                            {/* Protected Dashboards */}
                            <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />
                            <Route path="/admin-dashboard" element={<RoleRoute><AdminDashboard /></RoleRoute>} />
                            <Route path="/user-dashboard" element={<ProtectedRoute user={user}><UserDashboard /></ProtectedRoute>} />
                            {user && <Route path="/profile" element={<Profile />} />}

                            {/* Schools & Universities */}
                            <Route path="/canterbury-christ-church-university" element={<CanterburyChristChurchUni />} />
                            <Route path="/apex-college" element={<ApexCollegePage />} />
                            <Route path="/london-school-science-technology" element={<LSST />} />
                            <Route path="/roehampton" element={<RoehamptonPage />} />

                            {/* Vocational: SIA & Facilities */}
                            <Route path="/sia-course-details" element={<SIACourseOverview />} />
                            <Route path="/door-supervisor-dashboard" element={<DoorSupervisorDashboard />} />
                            <Route path="/door-supervisor-quiz" element={<DoorSupervisorQuizPage />} />
                            <Route path="/security-guard-dashboard" element={<SecurityGuardDashboard />} />
                            <Route path="/cctv-operator-dashboard" element={<CCTVOperatorDashboard />} />
                            <Route path="/facilities-management-overview" element={<FacilitiesManagementOverview />} />

                            {/* International Language Pages */}
                            <Route path="/nigerian-language" element={<NigeriaLanguagePage />} />
                            <Route path="/finnish-language" element={<FinnishLanguagePage />} />
                            <Route path="/german-language" element={<GermanyLanguage />} />
                            <Route path="/chinese-language" element={<ChineseLanguage />} />
                            <Route path="/english-for-professionals" element={<AdvancedEnglishProfessional />} />

                            {/* 404 Fallback */}
                            <Route path="*" element={
                                <Box sx={{ p: 10, textAlign: 'center' }}><Typography variant="h4">404: Page Not Found</Typography></Box>
                            } />
                        </Routes>
                    </Layout>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
};

export default App;
